/**
 * Reporting usage, without following anybody.
 *
 * The server is what actually enforces the privacy rules — it allow-lists paths
 * and event names, reduces referrers to a host, and derives a visitor hash from a
 * secret that rotates nightly. This side just has to be cheap and never get in the
 * way:
 *
 *  * events are batched and sent on a timer, so a tap never waits on a network call;
 *  * `sendBeacon` on page hide, which is the only way a browser reliably delivers
 *    anything as a tab closes;
 *  * every failure is swallowed. Analytics that can break the app is worse than no
 *    analytics.
 *
 * No cookie, no localStorage, no identifier of any kind is written here.
 *
 * The marketing site does NOT load this file — checked before `track` stopped
 * being a global. site/index.html has its own inline copy of the one event it
 * sends, written that way on purpose: one event per page load has nothing to
 * batch with, and see the comment there.
 */
const QUEUE = [];
let timer = null;

/**
 * The advertising campaign that brought this page load, if the URL said so.
 *
 * Read once and held in a module variable, which means it lives exactly as long as
 * the page does. Deliberately NOT written to localStorage, sessionStorage or a
 * cookie: any of those would be a stored identifier on the visitor's device, and
 * the privacy policy says there is not one. Attribution is not worth making that
 * sentence false.
 *
 * What it costs: someone who arrives from an ad, closes the tab and comes back the
 * next day signs up unattributed, so the funnel undercounts. That is the right way
 * round — an undercount is a worse number, a stored identifier is a broken promise.
 *
 * Whether a label is real is the server's decision, not this one: it matches it
 * against a closed allow-list and stores null for anything else, because a check
 * in a page anybody can edit protects nobody. The slice here is not that check —
 * it only keeps an absurdly long query parameter from failing the body schema and
 * taking a whole batch of legitimate events down with it.
 */
const CAMPAIGN =
  (new URLSearchParams(window.location.search).get('utm_campaign') || '').slice(0, 60) || undefined;

/*
 * And WHERE it came from, which is a different question from which campaign.
 *
 * One campaign runs on Instagram and on Facebook at the same time, so the
 * campaign name cannot separate them. Same handling as above and for the same
 * reason: the server allow-lists it, and this slice only stops an absurd query
 * parameter from failing the body schema and taking a batch of real events with
 * it.
 */
const SOURCE =
  (new URLSearchParams(window.location.search).get('utm_source') || '').slice(0, 40) || undefined;

function context() {
  return {
    // Only the referrer that brought someone to the site, not our own pages.
    referrer: document.referrer || undefined,
    locale: (navigator.language || 'es-ES').slice(0, 12),
    formFactor: window.matchMedia('(max-width: 760px)').matches ? 'mobile' : 'desktop',
    campaign: CAMPAIGN,
    source: SOURCE,
  };
}

function flush(useBeacon) {
  if (!QUEUE.length) return;
  const events = QUEUE.splice(0, QUEUE.length);
  const payload = JSON.stringify({ ...context(), events });

  try {
    if (useBeacon && navigator.sendBeacon) {
      // A Blob with the right type, because sendBeacon otherwise sends
      // text/plain and the API would reject it.
      navigator.sendBeacon('/api/events', new Blob([payload], { type: 'application/json' }));
      return;
    }
    fetch('/api/events', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: payload,
      credentials: 'same-origin',
      // The page may be going away; this must not hold it up.
      keepalive: true,
    }).catch(() => undefined);
  } catch (e) {
    // Swallowed on purpose — see the note at the top.
  }
}

/**
 * Records an event. Never throws, never blocks, never waits.
 *
 * Goes to two places, on purpose:
 *
 *  * **Umami**, so product events show up next to page views in the dashboard
 *    somebody actually opens. Guarded, because ad blockers commonly block
 *    cloud.umami.is and the app must not notice.
 *  * **Our own table**, because some questions can only be answered by joining to
 *    our data — "which searches returned nothing, and do we even have those cards
 *    in the catalogue?" is not something a hosted dashboard can answer, and the
 *    answer decides what to ingest next.
 */
export function track(name, params = {}) {
  try {
    if (window.umami && typeof window.umami.track === 'function') {
      window.umami.track(name, params.value != null ? { value: params.value } : undefined);
    }
  } catch (e) {
    // Blocked, not loaded, or changed its API. None of that is our problem.
  }

  QUEUE.push({ name, ...(params.path ? { path: params.path } : {}), ...(params.value != null ? { value: params.value } : {}) });
  if (QUEUE.length >= 20) return flush(false);
  if (timer) return;
  // Batched: a browse session fires several events in a second, and one request
  // for all of them is both cheaper and less to log.
  timer = setTimeout(() => { timer = null; flush(false); }, 4000);
}

// The tab going away is the one moment a pending batch would otherwise be lost.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flush(true);
});
window.addEventListener('pagehide', () => flush(true));

